import { useMemo } from "react"
import { formatDateString } from "@/v2-common/utils/utils.date"
import cn from "@/v2-ui/utils/utils.cn"
import Separator from "@/v2-ui/separator"
import { DotText } from "@/v2-backoffice-client/components/dot"
import Table, {
  createColumns,
  GROUPING_RANGE_DATES,
  type TableProps,
} from "@/v2-backoffice-client/components/table"
import { CUSTOMER_OWNERSHIP_RULE } from "@/v2-backoffice-client/customer/ownership/customer.ownership.utils"
import TenantUserRemoveManyButton from "@/v2-backoffice-client/tenant/user/remove/many/TenantUserRemoveManyButton"
import { formatUserName } from "@/v2-backoffice-client/user/user.utils"
import UserCustomerMismatchCustomerCell from "@/v2-backoffice-client/user/customerMismatch/UserCustomerMismatchCustomerCell"
import UserCustomerMismatchResolveCell from "@/v2-backoffice-client/user/customerMismatch/UserCustomerMismatchResolveCell"
import UserCustomerMismatchResolveManyButton from "@/v2-backoffice-client/user/customerMismatch/UserCustomerMismatchResolveManyButton"
import UserCustomerMismatchRowToolbar from "@/v2-backoffice-client/user/customerMismatch/UserCustomerMismatchRowToolbar"
import { useUserCustomerMismatchQuery } from "@/v2-backoffice-client/user/customerMismatch/user.customerMismatch.query"
import {
  describeMismatchChoices,
  formatMismatchCustomerName,
  getMismatchLikelyFix,
  MISMATCH_FIX_MERGED,
  MISMATCH_FIX_MIXED,
  MISMATCH_FIX_TEAM,
  MISMATCH_FIX_USER,
} from "@/v2-backoffice-client/user/customerMismatch/userCustomerMismatch.utils"
import type { UserCustomerMismatchFragment } from "@/v2-backoffice-client/user/customerMismatch/__types__/user.customerMismatch.fragment"

const COLUMN_SIZING = {
  User: 170,
  UserCustomer: 230,
  Tenant: 190,
  TeamCustomer: 230,
  PutRight: 360,
  LikelyFix: 150,
  Email: 200,
  TeamOwnership: 120,
  Role: 90,
  MembershipCreatedAt: 110,
}

const PUT_RIGHT_DESCRIPTION =
  "Every row has two ways out: move the user to where the team belongs (User → …), or move the team, every member of it, to where the user belongs (Team → …). The filled button is the way the team's other members point; the outlined one is the alternative. When HubSpot merged both customers' companies into one, there is one way: move both there (Both → …). Anything else is in the row menu: pick any customer, or take the user out of the team."

type CreateColumnsInput = {
  /** Called once a row's buttons have put it right; the board refetches. */
  onResolved(): void
}

function createUserCustomerMismatchColumns(input: CreateColumnsInput) {
  const { onResolved } = input
  return createColumns<UserCustomerMismatchFragment>({
    enableSelection: true,
    enableSorting: true,
    enableHiding: true,
    columns: {
      User: {
        id: "User",
        label: "User",
        filter: { type: "text", placeholder: "Name..." },
        getValue: (row) =>
          formatUserName(row.User?.FirstName, row.User?.LastName),
        to: ({ row }) => `?v=user:${row.original.UserId}`,
      },
      UserCustomer: {
        id: "UserCustomer",
        label: "Owned By",
        description:
          "The customer that owns the user (acc.Users.CustomerId): whose licenses and seats apply to them. Struck through with an arrow: HubSpot merged this customer's company into the one after the arrow, and that is where the user belongs.",
        enableGrouping: true,
        filter: { type: "text", placeholder: "Customer..." },
        getValue: (row) =>
          formatMismatchCustomerName(
            row.UserCustomerName,
            row.UserCustomerExternalId,
          ),
        renderCell({ row }) {
          return (
            <UserCustomerMismatchCustomerCell
              mismatch={row.original}
              side="user"
            />
          )
        },
        to: ({ row }) => {
          const externalId = row.original.UserCustomerExternalId
          if (!externalId) return
          return `?v=customer:${externalId}`
        },
      },
      Tenant: {
        id: "Tenant",
        label: "Team",
        description:
          "The team the user sits in, with how many live members it has: moving the team moves them all.",
        enableGrouping: true,
        filter: { type: "text", placeholder: "Team..." },
        getValue: (row) => row.Tenant?.Name ?? `#${row.TenantId}`,
        renderCell({ row }) {
          const count = row.original.TeamMemberCount
          return (
            <span className="inline-flex max-w-full items-baseline gap-1.5">
              <span className="truncate">
                {row.original.Tenant?.Name ?? `#${row.original.TenantId}`}
              </span>
              <span className="shrink-0 text-xs text-muted-foreground">
                {count} member{count === 1 ? "" : "s"}
              </span>
            </span>
          )
        },
        to: ({ row }) => `?v=tenant:${row.original.TenantId}`,
      },
      TeamCustomer: {
        id: "TeamCustomer",
        label: "Team Belongs To",
        description:
          "The customer the team is linked to (acc.Tenants.CustomerId). A member who registered through the team inherited it. Struck through with an arrow: HubSpot merged this customer's company into the one after the arrow, and that is where the team belongs.",
        enableGrouping: true,
        filter: { type: "text", placeholder: "Customer..." },
        getValue: (row) =>
          formatMismatchCustomerName(
            row.TeamCustomerName,
            row.TeamCustomerExternalId,
          ),
        renderCell({ row }) {
          return (
            <UserCustomerMismatchCustomerCell
              mismatch={row.original}
              side="team"
            />
          )
        },
        to: ({ row }) => {
          const externalId = row.original.TeamCustomerExternalId
          if (!externalId) return
          return `?v=customer:${externalId}`
        },
      },
      PutRight: {
        id: "PutRight",
        label: "Put Right",
        description: PUT_RIGHT_DESCRIPTION,
        enableSorting: false,
        isWrapped: true,
        filter: { type: "text", placeholder: "Customer..." },
        getValue: (row) => describeMismatchChoices(row),
        renderCell({ row }) {
          return (
            <UserCustomerMismatchResolveCell
              mismatch={row.original}
              onResolved={onResolved}
            />
          )
        },
      },
      LikelyFix: {
        id: "LikelyFix",
        label: "Likely Fix",
        description:
          "Merged in HubSpot: one or both customers' companies were merged into another company in the CRM, so the two sides are rows for one company and Put Right moves both there. Otherwise read off the team's other members. Team linked wrong: the team's customer owns none of its members, so the team is what moves. User owned wrong: the team's customer owns every other member, so the user is what moves. Mixed team: members from several customers, read the team before choosing.",
        enableGrouping: true,
        filter: {
          type: "multi-select",
          placeholder: "Any",
          options: [
            {
              label: MISMATCH_FIX_MERGED,
              value: MISMATCH_FIX_MERGED,
              dotVariant: "destructive",
            },
            {
              label: MISMATCH_FIX_TEAM,
              value: MISMATCH_FIX_TEAM,
              dotVariant: "warning",
            },
            {
              label: MISMATCH_FIX_USER,
              value: MISMATCH_FIX_USER,
              dotVariant: "orange",
            },
            {
              label: MISMATCH_FIX_MIXED,
              value: MISMATCH_FIX_MIXED,
              dotVariant: "muted",
            },
          ],
        },
        getValue: (row) => getMismatchLikelyFix(row),
        renderCell({ row }) {
          const fix = getMismatchLikelyFix(row.original)
          const variant =
            fix === MISMATCH_FIX_MERGED
              ? "destructive"
              : fix === MISMATCH_FIX_TEAM
                ? "warning"
                : fix === MISMATCH_FIX_USER
                  ? "orange"
                  : "muted"
          return <DotText variant={variant}>{fix}</DotText>
        },
      },
      Email: {
        id: "Email",
        label: "Email",
        filter: { type: "text", placeholder: "Email..." },
        getValue: (row) => row.User?.Email ?? "",
        to: ({ row }) => `?v=user:${row.original.UserId}`,
        defaultHidden: true,
      },
      TeamOwnership: {
        id: "TeamOwnership",
        label: "Owned by Team's Customer",
        description:
          "How many of the team's live members the team's customer owns, this user excluded from the count only when they are one of them.",
        getValue: (row) =>
          `${row.TeamMembersOwnedByTeamCustomer} of ${row.TeamMemberCount}`,
        exportValue({ row }) {
          return `${row.original.TeamMembersOwnedByTeamCustomer} of ${row.original.TeamMemberCount}`
        },
        defaultHidden: true,
      },
      Role: {
        id: "Role",
        label: "Role",
        enableGrouping: true,
        getValue: (row) => row.UserTenantType?.Name ?? "",
        defaultHidden: true,
      },
      MembershipCreatedAt: {
        id: "MembershipCreatedAt",
        label: "Member Since",
        filter: { type: "date-filter" },
        groupingRanges: GROUPING_RANGE_DATES,
        getValue: (row) => row.MembershipCreatedAt,
        renderCell({ row }) {
          const value = row.original.MembershipCreatedAt
          return value ? formatDateString(value) : null
        },
        exportValue({ row }) {
          return formatDateString(row.original.MembershipCreatedAt)
        },
        defaultHidden: true,
      },
      MembershipId: {
        id: "MembershipId",
        label: "User Team Id",
        defaultSize: 110,
        defaultHidden: true,
      },
    },
  })
}

export type UserCustomerMismatchTableProps = Omit<
  TableProps<UserCustomerMismatchFragment>,
  "data" | "columns"
>

/**
 * The Customer Mismatches worklist: every live membership whose user is
 * owned by another customer than the team's. One row per (user, team),
 * read left to right as a sentence - the user, who owns them, the team,
 * who the team belongs to - and put right in the Put Right column, where
 * the row's two ways out sit side by side as buttons with the suggested
 * one filled in. The selection bar is the same in bulk: each row's
 * suggested way, or one direction forced on every selected row. A row that
 * is intended (one company sharing content with another's user) is simply
 * left as it is. Owns the query, shared through the Apollo cache with the
 * tab trigger's count.
 */
function UserCustomerMismatchTable(props: UserCustomerMismatchTableProps) {
  const { className, ...tableProps } = props

  const { data, loading, error, refetch } = useUserCustomerMismatchQuery()
  const mismatches = data?.getUserCustomerMismatches ?? []

  const fixCounts = useMemo(() => {
    const counts = {
      [MISMATCH_FIX_MERGED]: 0,
      [MISMATCH_FIX_TEAM]: 0,
      [MISMATCH_FIX_USER]: 0,
      [MISMATCH_FIX_MIXED]: 0,
    }
    for (const row of mismatches) counts[getMismatchLikelyFix(row)] += 1
    return counts
  }, [mismatches])

  const columns = useMemo(
    () =>
      createUserCustomerMismatchColumns({
        onResolved() {
          void refetch()
        },
      }),
    [refetch],
  )

  function refresh() {
    void refetch()
  }

  return (
    <Table
      className={cn("user-customer-mismatch-table", className)}
      tableId="user-customer-mismatch-table"
      data={mismatches}
      columns={columns}
      initialColumnSizing={COLUMN_SIZING}
      enableGrouping
      enableRowSelection
      {...tableProps}
    >
      <Table.Toolbar>
        {/* Namespaced: the board shares the Users page with the users
            board, so the default "status" param must not leak between the
            two tabs. */}
        <Table.StatusFilter
          column="LikelyFix"
          urlParam="mismatch-fix"
          isLoading={loading}
        >
          <Table.StatusFilter.Option
            value={null}
            count={mismatches.length}
            title={CUSTOMER_OWNERSHIP_RULE}
          >
            All
          </Table.StatusFilter.Option>

          <Table.StatusFilter.Option
            value={0}
            filterValue={MISMATCH_FIX_MERGED}
            variant="destructive"
            iconSrc="shuffle"
            count={fixCounts[MISMATCH_FIX_MERGED]}
            title="HubSpot merged one or both customers' companies into another company: the two sides are rows for one company, and the surviving customer is where both belong. Put Right offers that one move."
          >
            Merged In HubSpot
          </Table.StatusFilter.Option>

          <Table.StatusFilter.Option
            value={1}
            filterValue={MISMATCH_FIX_TEAM}
            variant="warning"
            iconSrc="triangle-warning"
            count={fixCounts[MISMATCH_FIX_TEAM]}
            title="The team's customer owns none of the team's members: the team is linked to the wrong customer. Team → the user's customer is suggested; one click puts every row of the team right."
          >
            Team Linked Wrong
          </Table.StatusFilter.Option>

          <Table.StatusFilter.Option
            value={2}
            filterValue={MISMATCH_FIX_USER}
            count={fixCounts[MISMATCH_FIX_USER]}
            title="Every other member of the team is owned by the team's customer: this one user is owned by the wrong one. User → the team's customer is suggested."
          >
            User Owned Wrong
          </Table.StatusFilter.Option>

          <Table.StatusFilter.Option
            value={3}
            filterValue={MISMATCH_FIX_MIXED}
            count={fixCounts[MISMATCH_FIX_MIXED]}
            title="Members from several customers in one team: read the team before choosing. Neither way is suggested, and some of these are intended sharing."
          >
            Mixed Team
          </Table.StatusFilter.Option>
        </Table.StatusFilter>

        <div className="flex-1" />
        <Table.Search placeholder="Search by name, email, team or customer..." />

        <Table.ToolbarFilters />
        <Separator orientation="vertical" />

        <Table.ToolbarMenu
          onRefresh={refresh}
          exportFileName="user-customer-mismatches"
        />
      </Table.Toolbar>

      <Table.FilterList />
      <Table.GroupList />

      <Table.RowToolbar<UserCustomerMismatchFragment> column="User">
        {(mismatch) => (
          <UserCustomerMismatchRowToolbar
            mismatch={mismatch}
            onCompleted={refresh}
          />
        )}
      </Table.RowToolbar>

      <Table.Content
        countLabel="mismatches"
        isLoading={loading}
        error={error}
      />

      <Table.SelectionToolbar<UserCustomerMismatchFragment>>
        {(rows, reset) => {
          function resetAndRefresh() {
            reset()
            refresh()
          }
          return (
            <>
              <Table.ExportSelectionButton
                fileName="user-customer-mismatches"
                selectionCount={rows.length}
              />

              <Separator orientation="vertical" />

              <UserCustomerMismatchResolveManyButton
                strategy="suggested"
                mismatches={rows}
                onCompleted={resetAndRefresh}
              />

              <UserCustomerMismatchResolveManyButton
                strategy="users-to-teams"
                mismatches={rows}
                onCompleted={resetAndRefresh}
              />

              <UserCustomerMismatchResolveManyButton
                strategy="teams-to-users"
                mismatches={rows}
                onCompleted={resetAndRefresh}
              />

              <Separator orientation="vertical" />

              <TenantUserRemoveManyButton
                isStacked
                userTenantIds={rows.map((row) => row.MembershipId)}
                onCompleted={resetAndRefresh}
              >
                Remove
              </TenantUserRemoveManyButton>
            </>
          )
        }}
      </Table.SelectionToolbar>
    </Table>
  )
}

export default UserCustomerMismatchTable
